import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MapareRow, NomenclatorEntry } from '@lyra/shared';
import { apiFetch } from '@/lib/apiClient';
import { queryKeys } from '@/lib/queryKeys';
import { formatNumberRo } from '@/lib/format';
import { notifyError, notifySuccess } from '@/lib/useMutationToast';
import { normalizeClassifyResponse, type ClassifyResponseRaw } from '@/features/mapari/lib/classify';

/**
 * Mapări review/correction mutation + search hooks (Plan 3c-2 Task 3).
 * All four endpoints are already implemented server-side and org-scoped
 * (server/src/routes/{nomenclator,classifications,runs,classify}.ts) — no
 * server changes here. See task-3-report.md for the verified contract of
 * each endpoint and any adaptation made vs. the task brief.
 */

/**
 * `GET /nomenclator?search=&limit=` search, used by the correction panel's
 * (T4) manual nomenclator lookup. `enabled` gates on a 2-char minimum (avoids
 * firing on a single keystroke); `placeholderData` keeps the previous
 * result list on screen while a new keystroke's query is in flight, instead
 * of flashing to a loading state.
 *
 * Response shape verified against `data.listNomenclator`
 * (server/src/db/data.ts ~L698-741): `{ items: [...], total }`, where each
 * item is `{ id, codBdMedoc, codMedoc, denumireMedoc }` — matches the task
 * brief's assumed `{items:[...]}` shape exactly, just with an extra `total`
 * that the brief didn't mention (added here since the server always returns
 * it; harmless to type and ignore if unused).
 */
export function useNomenclatorSearch(term: string) {
  return useQuery({
    queryKey: queryKeys.nomenclator(term),
    queryFn: () =>
      apiFetch<{ items: NomenclatorEntry[]; total: number }>(
        `/nomenclator?search=${encodeURIComponent(term)}&limit=15`
      ),
    enabled: term.trim().length >= 2,
    placeholderData: (prev) => prev,
  });
}

interface ResolveEntryVars {
  id: number;
  patch: Partial<MapareRow>;
  /** Optional: when provided, invalidates that run's detail query on success. */
  runId?: number;
  /**
   * Suppresses this call's toast. Set only by the „Ambigue" bulk bar, which
   * resolves every selected row in a loop — one toast per row would bury the
   * screen (same contract as `useSplitEntry`'s `silent` and `useRetryRows`,
   * which is unconditionally quiet for the identical reason).
   */
  silent?: boolean;
}

/**
 * `PATCH /classifications/:id` — generic single-row correction, used by the
 * T4 correction panel (manual edit, nomenclator-pick, mark-incorrect, etc.).
 * Whitelist + org-scoping verified against `data.updateClassification`
 * (server/src/db/data.ts ~L140-204) — see task-3-report.md for the full
 * field list. Of the brief's listed fields, all are accepted
 * (`reviewResolution`, `codMedoc`, `denumireMedoc`, `codBdMedoc`, `status`,
 * `primaryCandidate`/`alternateCandidates`) EXCEPT `originalCodMedoc` /
 * `originalDenumireMedoc`: there is no DB column or mapping for either at
 * all — `toClassificationRow` hardcodes both to `undefined` in every read,
 * and the PATCH whitelist has no case for them either. A patch that sets
 * them is silently a no-op (not a crash) and a refetch will never show them
 * populated. Flagged as a concern in the report — Task 1's
 * `buildEditPayload` (the amber "Clasificare originală" audit note) relies
 * on these fields persisting, which the server does not currently support.
 */
export function useResolveEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: ResolveEntryVars) =>
      apiFetch<MapareRow>(`/classifications/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: (_data, { runId, patch, silent }) => {
      if (runId != null) qc.invalidateQueries({ queryKey: queryKeys.run(runId) });
      if (silent) return;
      // Swept every `useResolveEntry` call site (2026-08-19 remediation
      // Slice C, Task 2 review fix, round 2) — three distinct patch shapes
      // reach this one hook, and reusing "Rândul a fost corectat." for all
      // three would misdescribe two of them:
      //  - `reviewResolution: 'unresolved'` — `WizardReviewStep`'s
      //    „Marchează nerezolvat" and `CorrectionPanel`'s „Fără corespondent".
      //  - `status: 'error'` (+ `accepted: false`) — „Marchează incorect",
      //    sent from BOTH `CorrectionPanel.tsx` and `WizardListStep.tsx`'s
      //    own `markIncorrect`. `status: 'error'` doesn't appear in any
      //    other patch sent to this hook (confirmed_primary/selected_alternate/
      //    manual edits all use 'edited'/'classified'), so it discriminates
      //    cleanly without risk of mislabelling a genuine edit.
      //  - everything else (confirm primary, pick an alternate, manual edit,
      //    nomenclator-search pick) is a real correction — default message.
      const message =
        patch.reviewResolution === 'unresolved'
          ? 'Rândul a fost marcat nerezolvat.'
          : patch.status === 'error'
            ? 'Rândul a fost marcat incorect.'
            : 'Rândul a fost corectat.';
      notifySuccess(message);
    },
    onError: (error, { silent }) => {
      if (silent) return;
      notifyError(error, 'Corectarea nu a reușit.');
    },
  });
}

/** Resolution values accepted by `POST /runs/:id/entries/bulk-resolve` that T5/T6's bulk toolbar uses. */
export type BulkResolution = 'accepted' | 'no_match_confirmed';

/**
 * `POST /runs/:id/entries/bulk-resolve` — bulk-accept / bulk-confirm-no-match
 * for the interactive table's multi-select toolbar. Body + allowed values
 * verified against `server/src/routes/runs.ts` + `data.bulkResolveEntries`
 * (server/src/db/data.ts ~L868-896) — see task-3-report.md.
 */
export function useBulkResolve(runId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, resolution }: { ids: number[]; resolution: BulkResolution }) =>
      apiFetch<{ count: number }>(`/runs/${runId}/entries/bulk-resolve`, {
        method: 'POST',
        body: JSON.stringify({ ids, resolution }),
      }),
    onSuccess: (data, { ids }) => {
      qc.invalidateQueries({ queryKey: queryKeys.run(runId) });
      const count = data?.count ?? ids.length;
      notifySuccess(`${formatNumberRo(count)} ${count === 1 ? 'rând validat' : 'rânduri validate'}.`);
    },
    onError: (error) => notifyError(error, 'Validarea nu a reușit.'),
  });
}

/**
 * `POST /classify` → normalize (Task 1's `normalizeClassifyResponse`) →
 * `PATCH /classifications/:id`, one row at a time. Task 5's UI orchestrates
 * concurrency/looping over multiple rows and reacts to a thrown
 * `ApiClientError` (esp. `.status === 403`, "no active instance session")
 * — this hook does not catch or swallow it, it propagates straight out of
 * `mutateAsync`.
 *
 * Final-review fix (Rule Zero): the writeback is gated on `workflowStatus`,
 * matching v1 `useClassification.ts`'s `retryErrorRows` (~L579-619) exactly —
 * a `resolved` retry adopts the firm `codMedoc`/`denumireMedoc`/`codBdMedoc`
 * and is auto-stamped `reviewResolution:'confirmed_primary'`; any other
 * workflow status (`ambiguous`/`needs_review`/`no_match`/`low_confidence`)
 * only stores the model's guess in `provisionalCodMedoc`/
 * `provisionalDenumireMedoc`/`provisionalCodBdMedoc`, leaves the firm codes
 * empty, and leaves `reviewResolution` untouched (v1 writes `null` to local
 * React state; here that's simply omitting the key from the PATCH body, since
 * `updateClassification`'s whitelist already treats an absent key as "no
 * change" — `rowsNeedingRetry` only selects rows with `reviewResolution ==
 * null` to begin with, so the observable effect is identical). `confidence`,
 * `reasoning`, `workflowStatus`, `primaryCandidate`, `alternateCandidates`
 * stay unconditional (v1 sets those regardless of resolution); `status:
 * 'classified'` is added on top (v1 always marks a successfully-reclassified
 * row `'classified'` regardless of the resulting workflow status).
 *
 * Deliberately NOT toasted (2026-08-19 remediation Slice C, Task 2): this
 * mutation fires once PER ROW inside `RetryBar.tsx`'s bounded-concurrency
 * batch loop (`RETRY_CONCURRENCY = 3`), so a "Reîncearcă toate" over dozens
 * of error rows would spam a toast per row. That flow already has its own
 * feedback (`Se reîncearcă… X/Y` progress, the 403 „Pornește instanța" CTA)
 * — see `RetryBar.tsx`.
 */
export function useRetryRows(runId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: MapareRow) => {
      const raw = await apiFetch<ClassifyResponseRaw>('/classify', {
        method: 'POST',
        body: JSON.stringify({ text: row.name, model: 'mistral-small3.2' }),
      });
      const normalized = normalizeClassifyResponse(raw);
      const resolved = normalized.workflowStatus === 'resolved';
      const patch: Partial<MapareRow> = {
        confidence: normalized.confidence,
        reasoning: normalized.reasoning,
        workflowStatus: normalized.workflowStatus,
        primaryCandidate: normalized.primaryCandidate,
        alternateCandidates: normalized.alternateCandidates,
        status: 'classified',
        codMedoc: resolved ? normalized.codMedoc : '',
        denumireMedoc: resolved ? normalized.denumireMedoc : '',
        codBdMedoc: resolved ? normalized.codBdMedoc : undefined,
        provisionalCodMedoc: normalized.codMedoc,
        provisionalDenumireMedoc: normalized.denumireMedoc,
        provisionalCodBdMedoc: normalized.codBdMedoc,
        ...(resolved ? { reviewResolution: 'confirmed_primary' as const } : {}),
      };
      return apiFetch<MapareRow>(`/classifications/${row.id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.run(runId) });
    },
  });
}

/**
 * `POST /runs/:id/entries/:entryId/split` (Task 2's server route) — used by
 * Task 5's `ambiguu`-step "Generează separat" button. Splits one ambiguous
 * entry into one `run_entry` per MEDOC candidate (linked via
 * `parent_entry_id`), stamps the original `review_resolution:'expanded'`.
 * `201 { count, items }`; the server 400s `too_few` (<2 candidates) and
 * 409s on a finalized run — `WizardReviewStep`'s `canSplitRow` guard
 * disables the button in both cases so those never fire from the UI.
 *
 * Invalidates the run detail (the split original leaves „Ambigue" via its
 * new `reviewResolution`, its children appear under „Revizuire") and the
 * runs list. The list invalidation uses the bare `['runs']` prefix, not
 * `queryKeys.runs()` — `queryKeys.runs(status, limit)` takes call-site
 * args (the main `/mapari` list vs. `/utilizare`'s narrower `limit`), so a
 * single fixed-arg call wouldn't prefix-match every list variant; `['runs']`
 * does, matching `runHooks.ts`'s own `useInvalidateRuns` convention for the
 * exact same reason.
 */
export function useSplitEntry(runId: number) {
  const qc = useQueryClient();
  return useMutation({
    // `candidateIndexes` (2026-08-21 UI report) is the operator's per-candidate
    // tick list, 0-based over `[primary, ...alternates]` — omitted means "split
    // every candidate", the server's own default. `silent` suppresses the
    // per-row toast when this fires inside the „Ambigue" bulk loop, which would
    // otherwise raise one toast per row (same reasoning as `useRetryRows`).
    mutationFn: ({ id, candidateIndexes }: { id: number; candidateIndexes?: number[]; silent?: boolean }) =>
      apiFetch<{ count: number; items: MapareRow[] }>(`/runs/${runId}/entries/${id}/split`, {
        method: 'POST',
        body: JSON.stringify(candidateIndexes ? { candidateIndexes } : {}),
      }),
    onSuccess: (data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.run(runId) });
      qc.invalidateQueries({ queryKey: ['runs'] });
      if (variables.silent) return;
      notifySuccess(
        data?.count != null ? `Rândul a fost separat în ${formatNumberRo(data.count)} rânduri.` : 'Rândul a fost separat.'
      );
    },
    onError: (error, variables) => {
      if (variables.silent) return;
      notifyError(error, 'Separarea rândului a eșuat.');
    },
  });
}
